import { useState } from "react";
import { bursaNews } from "@/mocks/bursaNews";

const categories = ["Tümü", "Turizm", "Ulaşım", "Kültür", "Spor", "Ekonomi", "Tarih"];

const categoryColors: Record<string, string> = {
  Turizm: "bg-emerald-100 text-emerald-700",
  Ulaşım: "bg-amber-100 text-amber-700",
  Kültür: "bg-violet-100 text-violet-700",
  Spor: "bg-rose-100 text-rose-700",
  Ekonomi: "bg-blue-100 text-blue-700",
  Tarih: "bg-stone-100 text-stone-700",
};

export default function BursaNewsSection() {
  const [activeCategory, setActiveCategory] = useState("Tümü");

  const filtered = activeCategory === "Tümü"
    ? bursaNews
    : bursaNews.filter((n) => n.category === activeCategory);

  const featured = filtered[0];
  const rest = filtered.slice(1, 5);

  return (
    <section className="py-14 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 tracking-tight">Bursa Haberleri</h2>
            <p className="text-stone-500 text-sm mt-1">Şehirdeki son gelişmelerden haberdar ol</p>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-stone-800 text-white"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-stone-400 text-center py-12">Bu kategoride haber bulunamadı.</p>
        )}

        {featured && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* Featured */}
            <div className="lg:col-span-3 group cursor-pointer">
              <div className="rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all">
                <div className="w-full h-56 overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${categoryColors[featured.category] ?? "bg-stone-100 text-stone-600"}`}>
                      {featured.category}
                    </span>
                    <span className="text-xs text-stone-400">{featured.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-800 leading-snug mb-2 group-hover:text-emerald-700 transition-colors">
                    {featured.title}
                  </h3>
                  <p className="text-stone-500 text-sm leading-relaxed line-clamp-3">{featured.summary}</p>
                  <p className="text-xs text-stone-400 mt-3 flex items-center gap-1">
                    <i className="ri-newspaper-line" /> {featured.source}
                  </p>
                </div>
              </div>
            </div>

            {/* Side List */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              {rest.map((news) => (
                <div key={news.id} className="group cursor-pointer flex gap-3 p-3 rounded-xl hover:bg-stone-50 transition-all border border-transparent hover:border-stone-100">
                  <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColors[news.category] ?? "bg-stone-100 text-stone-600"}`}>
                        {news.category}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-stone-800 leading-snug group-hover:text-emerald-700 transition-colors line-clamp-2">
                      {news.title}
                    </h4>
                    <p className="text-xs text-stone-400 mt-1">{news.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
